import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Switch,
  TextField,
  Typography,
  Alert,
  Tooltip,
} from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSelector } from 'react-redux';
import { isPaidPlan, isProPlan, normalizePlan } from '../../utils/plans';
import {
  patchBoardAccess,
  rotateBoardShareToken,
  inviteBoardCollaborator,
  revokeBoardCollaborator,
  acceptBoardInvite,
} from '../api/boardApi';
import { useBoardStore } from '../store/useBoardStore';

function buildPublicUrl(username, hashtag) {
  return `${window.location.origin}/${encodeURIComponent(username)}/${encodeURIComponent(hashtag)}`;
}

function buildPrivateUrl(username, hashtag, shareToken) {
  const base = buildPublicUrl(username, hashtag);
  if (!shareToken) return base;
  return `${base}?token=${encodeURIComponent(shareToken)}`;
}

/**
 * Owner share / privacy / co-edit panel.
 */
export default function BoardSharePanel({
  personName,
  hashtag,
  isOwner,
  role,
}) {
  const user = useSelector(state => state.user);
  const board = useBoardStore(s => s.board);
  const loadBoard = useBoardStore(s => s.loadBoard);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [copied, setCopied] = useState(false);

  const plan = normalizePlan(user?.subscriptionStatus);
  const canPrivate = isPaidPlan(plan);
  const canCoEdit = isProPlan(plan);
  const isPrivate = board?.accessRestriction === 'PRIVATE';

  const shareUrl = useMemo(() => {
    if (!personName || !hashtag) return '';
    if (isPrivate) return buildPrivateUrl(personName, hashtag, board?.shareToken);
    return buildPublicUrl(personName, hashtag);
  }, [personName, hashtag, isPrivate, board?.shareToken]);

  const collaborators = useMemo(
    () => (board?.collaborators || []).filter(c => c.status !== 'revoked'),
    [board?.collaborators]
  );

  // Auto-accept invite deep link for Pro co-editors
  useEffect(() => {
    if (!user || isOwner) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('invite') !== '1') return;
    let cancelled = false;
    (async () => {
      try {
        await acceptBoardInvite(personName, hashtag);
        if (!cancelled) await loadBoard(personName, hashtag, { force: true });
      } catch (err) {
        if (!cancelled) console.warn('Accept invite failed', err?.message || err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, isOwner, personName, hashtag, loadBoard]);

  const handleTogglePrivate = useCallback(async () => {
    if (!isOwner || !canPrivate) {
      setError(canPrivate ? '' : 'Private boards require Hobby or Pro.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const next = isPrivate ? 'PUBLIC' : 'PRIVATE';
      const data = await patchBoardAccess(hashtag, next);
      useBoardStore.setState(state => ({
        board: state.board
          ? {
              ...state.board,
              accessRestriction: data.board?.accessRestriction || next,
              shareToken: data.shareToken || data.board?.shareToken || state.board.shareToken,
            }
          : state.board,
      }));
    } catch (err) {
      setError(err.message || 'Failed to update access');
    } finally {
      setBusy(false);
    }
  }, [isOwner, canPrivate, isPrivate, hashtag]);

  const handleRotate = useCallback(async () => {
    if (!isOwner || !isPrivate) return;
    setBusy(true);
    setError('');
    try {
      const data = await rotateBoardShareToken(hashtag);
      useBoardStore.setState(state => ({
        board: state.board
          ? { ...state.board, shareToken: data.shareToken }
          : state.board,
      }));
    } catch (err) {
      setError(err.message || 'Failed to rotate link');
    } finally {
      setBusy(false);
    }
  }, [isOwner, isPrivate, hashtag]);

  const handleCopy = useCallback(async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setError('Could not copy link');
    }
  }, [shareUrl]);

  const handleInvite = useCallback(async () => {
    if (!canCoEdit) {
      setError('Co-edit requires Pro. Invitees must also be Pro.');
      return;
    }
    const email = inviteEmail.trim();
    if (!email) return;
    setBusy(true);
    setError('');
    try {
      const data = await inviteBoardCollaborator(hashtag, email);
      useBoardStore.setState(state => ({
        board: state.board
          ? { ...state.board, collaborators: data.collaborators }
          : state.board,
      }));
      setInviteEmail('');
    } catch (err) {
      setError(err.message || 'Invite failed');
    } finally {
      setBusy(false);
    }
  }, [canCoEdit, inviteEmail, hashtag]);

  const handleRevoke = useCallback(async (collab) => {
    setBusy(true);
    setError('');
    try {
      const data = await revokeBoardCollaborator(hashtag, {
        email: collab.email,
        userId: collab.userId,
      });
      useBoardStore.setState(state => ({
        board: state.board
          ? { ...state.board, collaborators: data.collaborators }
          : state.board,
      }));
    } catch (err) {
      setError(err.message || 'Revoke failed');
    } finally {
      setBusy(false);
    }
  }, [hashtag]);

  if (!isOwner && role !== 'editor') return null;

  return (
    <>
      <Tooltip title="Share this memory">
        <IconButton
          size="small"
          onClick={() => setOpen(true)}
          aria-label="Share canvas"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 5,
            bgcolor: 'background.paper',
            boxShadow: 1,
          }}
        >
          <ShareIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Share this memory</DialogTitle>
        <DialogContent>
          {error ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          {isOwner ? (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={isPrivate}
                    onChange={handleTogglePrivate}
                    disabled={busy || !canPrivate}
                  />
                }
                label={isPrivate ? 'Private (secret link)' : 'Public (anyone with URL)'}
              />
              {!canPrivate ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Upgrade to Hobby or Pro to make this board private.
                </Typography>
              ) : null}

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1, mb: 2 }}>
                <TextField
                  size="small"
                  fullWidth
                  value={shareUrl}
                  InputProps={{ readOnly: true }}
                />
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={handleCopy}
                  disabled={!shareUrl}
                >
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </Box>
              {isPrivate ? (
                <Button size="small" onClick={handleRotate} disabled={busy}>
                  Rotate secret link
                </Button>
              ) : null}

              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
                Co-editors (Pro)
              </Typography>
              {!canCoEdit ? (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Upgrade to Pro to invite other Pro users to edit together.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    type="email"
                    placeholder="colleague@example.com"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                  />
                  <Button variant="contained" onClick={handleInvite} disabled={busy || !inviteEmail.trim()}>
                    Invite
                  </Button>
                </Box>
              )}

              <List dense>
                {collaborators.map(c => (
                  <ListItem key={`${c.email}-${c.userId || ''}`}>
                    <ListItemText
                      primary={c.username || c.email}
                      secondary={`${c.email} · ${c.status}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="Revoke"
                        onClick={() => handleRevoke(c)}
                        disabled={busy}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
                {!collaborators.length ? (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                    No collaborators yet.
                  </Typography>
                ) : null}
              </List>
            </>
          ) : (
            <Typography variant="body2">
              You are a co-editor on this canvas. Changes sync in real time with other editors.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
